export type AnalyticsValue = string | number | boolean;
export type AnalyticsPayload = Record<string, AnalyticsValue | null | undefined>;

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim();
const DEBUG_MODE = (() => {
  const value = (import.meta.env.VITE_GA_DEBUG as string | undefined)?.trim().toLowerCase();
  return value === "1" || value === "true" || value === "yes";
})();

const BLOCKED_KEYS = new Set<string>([
  "name",
  "display_name",
  "full_name",
  "email",
  "title",
  "description",
  "context",
  "detail",
  "error",
  "message",
  "query",
  "search",
  "invitee_email",
  "invitee_name",
  "task_title",
  "task_description"
]);

let initialized = false;
let scriptLoadPromise: Promise<void> | null = null;

const hasWindow = (): boolean => typeof window !== "undefined";

const toSnakeCase = (value: string): string =>
  value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase();

const isBlockedKey = (key: string): boolean => {
  const normalized = toSnakeCase(key);
  if (BLOCKED_KEYS.has(normalized)) return true;
  if (normalized.endsWith("_email")) return true;
  if (normalized.endsWith("_name")) return true;
  return false;
};

const sanitizeValue = (value: AnalyticsValue): AnalyticsValue | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    // Keep payloads compact and reduce accidental free-text leakage.
    return trimmed.length > 120 ? trimmed.slice(0, 120) : trimmed;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return undefined;
    return value;
  }
  return value;
};

export const sanitizeAnalyticsPayload = (payload: AnalyticsPayload): Record<string, AnalyticsValue> => {
  const sanitized: Record<string, AnalyticsValue> = {};
  for (const [rawKey, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    const key = toSnakeCase(rawKey);
    if (!key || isBlockedKey(key)) continue;
    const nextValue = sanitizeValue(value);
    if (nextValue === undefined) continue;
    sanitized[key] = nextValue;
  }
  return sanitized;
};

const bootstrapDataLayer = (): void => {
  if (!hasWindow()) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer.push(args);
    };
  }
};

const loadScript = (): Promise<void> => {
  if (!hasWindow()) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-ga4-id=\"${MEASUREMENT_ID}\"]`);
    if (existing) {
      if (existing.dataset.loaded === "1") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => resolve(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID || "")}`;
    script.dataset.ga4Id = MEASUREMENT_ID || "";
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "1";
        resolve();
      },
      { once: true }
    );
    script.addEventListener("error", () => resolve(), { once: true });
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

export const isAnalyticsEnabled = (): boolean => Boolean(MEASUREMENT_ID);

export const initAnalytics = async (): Promise<boolean> => {
  if (!MEASUREMENT_ID || !hasWindow()) return false;
  if (initialized) return true;

  bootstrapDataLayer();
  await loadScript();

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    ...(DEBUG_MODE ? { debug_mode: true } : {})
  });

  initialized = true;
  return true;
};

const emit = (command: string, ...args: unknown[]): void => {
  if (!MEASUREMENT_ID || !hasWindow()) return;
  bootstrapDataLayer();
  if (!initialized) {
    void initAnalytics();
  }
  window.gtag(command, ...args);
};

export const trackEvent = (eventName: string, payload: AnalyticsPayload = {}): void => {
  const sanitized = sanitizeAnalyticsPayload(payload);
  emit("event", eventName, sanitized);
};

export const trackPageView = (payload: AnalyticsPayload): void => {
  trackEvent("page_view", payload);
};

export const setAnalyticsUser = (userId: string | undefined): void => {
  if (!MEASUREMENT_ID) return;
  emit("config", MEASUREMENT_ID, {
    user_id: userId || undefined,
    ...(DEBUG_MODE ? { debug_mode: true } : {})
  });
};

export const setAnalyticsUserProperties = (payload: AnalyticsPayload): void => {
  const sanitized = sanitizeAnalyticsPayload(payload);
  emit("set", "user_properties", sanitized);
};

export const __resetAnalyticsForTests = (): void => {
  initialized = false;
  scriptLoadPromise = null;
};
